import { landingThemeConfig } from './landingThemeConfig';

/**
 * Centralized demo container styling configuration
 * This ensures exact visual parity between the original demo container
 * in the landing page and the demo sub-pages
 */
export const getDemoContainerStyles = (colorTheme = 'teal') => {
  const studentTheme = landingThemeConfig.student(colorTheme);
  
  return {
  // Outer container wrapper
  outerWrapper: {
    width: '100%', 
    maxWidth: '100%', // Let parent Container handle max width
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  },

  // Main demo container (Paper)
  demoContainer: {
    bgcolor: studentTheme.background.darker,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: `0 0 40px ${studentTheme.components.demo.glow}`, // Reduced - gradient handles main glow
    backdropFilter: 'blur(10px)', // Frosted glass effect
    // Original uses aspectRatio for landing page, sub-pages use minHeight
    // We'll make this configurable
  },

  // Responsive sizing for landing page (original)
  demoContainerLandingPage: {
    aspectRatio: { xs: '16/20', md: '16/14' },
    minHeight: { xs: '600px', sm: '700px', md: '800px' },
    maxHeight: { xs: '700px', sm: '800px', md: '900px' },
  },

  // Responsive sizing for sub-pages (larger for full-screen experience)
  demoContainerSubPage: {
    minHeight: { xs: '800px', sm: '900px', md: '1000px' },
  },

  // Window header bar
  windowHeader: {
    height: 40,
    bgcolor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    px: 2,
  },

  // Window buttons
  windowButtons: {
    red: { 
      width: 12, 
      height: 12, 
      borderRadius: '50%', 
      bgcolor: studentTheme.components.demo.windowButtons.red 
    },
    yellow: { 
      width: 12, 
      height: 12, 
      borderRadius: '50%', 
      bgcolor: studentTheme.components.demo.windowButtons.yellow 
    },
    green: { 
      width: 12, 
      height: 12, 
      borderRadius: '50%', 
      bgcolor: studentTheme.components.demo.windowButtons.green 
    },
  },

  // Window URL typography
  windowUrl: {
    ml: 2, 
    color: 'text.secondary',
    fontFamily: 'monospace',
    display: { xs: 'none', sm: 'block' },
  },

  // Demo content area
  demoContent: {
    height: 'calc(100% - 40px)',
    bgcolor: studentTheme.background.darker
  },

  // Demo content area for landing page (original uses flex)
  demoContentLandingPage: {
    height: 'calc(100% - 40px)',
    display: 'flex',
    overflow: { xs: 'auto', md: 'hidden' }, // Scroll on mobile, hidden on desktop
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Demo content area for sub-pages (uses scroll)
  demoContentSubPage: {
    height: 'calc(100% - 40px)',
    overflow: 'auto',
    bgcolor: studentTheme.background.darker
  },

  // Glow effect overlay (this was missing from sub-pages!)
  glowEffect: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120%',
    height: '120%',
    background: `radial-gradient(circle, ${studentTheme.components.demo.glowBlur} 0%, transparent 70%)`,
    pointerEvents: 'none',
    filter: 'blur(40px)',
  },

  // Background for sub-pages (should match landing page exactly)
  subPageBackground: {
    minHeight: '100vh',
    backgroundColor: studentTheme.background.default, // Use solid background like landing page
    position: 'relative',
    overflow: 'visible',
  },

  // Container wrapper for sub-pages (adds padding)
  subPageContainer: {
    py: 4
  }
  };
};

/**
 * Utility function to get complete demo container component structure
 * @param {Object} props - Configuration props
 * @param {string} props.mode - 'landing' or 'subpage'
 * @param {string} props.urlText - Text to display in window URL bar
 * @param {string} props.colorTheme - Color theme to use
 * @param {React.ReactNode} props.children - Content to render inside the demo container
 * @returns {Object} - Complete demo container structure
 */
export const getDemoContainerProps = ({ mode = 'landing', urlText = 'demo.examr.ai', colorTheme = 'teal' } = {}) => {
  const isLandingPage = mode === 'landing';
  const demoContainerStyles = getDemoContainerStyles(colorTheme);
  
  return {
    outerWrapper: demoContainerStyles.outerWrapper,
    demoContainer: {
      ...demoContainerStyles.demoContainer,
      ...(isLandingPage 
        ? demoContainerStyles.demoContainerLandingPage 
        : demoContainerStyles.demoContainerSubPage
      )
    },
    windowHeader: demoContainerStyles.windowHeader,
    windowButtons: demoContainerStyles.windowButtons,
    windowUrl: demoContainerStyles.windowUrl,
    demoContent: isLandingPage 
      ? demoContainerStyles.demoContentLandingPage 
      : demoContainerStyles.demoContentSubPage,
    glowEffect: demoContainerStyles.glowEffect,
    urlText,
  };
};

/**
 * Utility function to get consistent page background styling
 * @param {Object} props - Configuration props
 * @param {string} props.mode - 'landing' or 'subpage'
 * @param {string} props.colorTheme - Color theme to use
 * @returns {Object} - Page background styles
 */
export const getPageBackgroundStyles = ({ mode = 'landing', colorTheme = 'teal' } = {}) => {
  const demoContainerStyles = getDemoContainerStyles(colorTheme);
  
  if (mode === 'subpage') {
    return {
      pageBackground: demoContainerStyles.subPageBackground,
      containerWrapper: demoContainerStyles.subPageContainer,
    };
  }
  
  // Landing page uses different background structure
  return {
    pageBackground: {
      minHeight: '100vh',
      backgroundColor: 'background.default',
      position: 'relative',
      overflow: 'visible',
    },
    containerWrapper: {}, // Landing page doesn't need container wrapper padding
  };
};

// Export default styles with default theme for backward compatibility
export const demoContainerStyles = getDemoContainerStyles('teal');
export default demoContainerStyles;
